import { useState } from "react";
import AdminLayout from "@/layouts/AdminLayout";
import { useApi } from "@/hooks/useApi";
import TicketModal from "../../dashboard/tickets/components/TicketModal";
import api from "@/services/api";

interface Ticket {
  id: string;
  subject: string;
  message: string;
  status: string;
  priority: string;
  project_id: string | null;
  created_at: string;
}

export default function AdminTickets() {
  const [statusFilter, setStatusFilter] = useState("");
  const [viewModal, setViewModal] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  const query = statusFilter ? `/tickets?status=${statusFilter}` : "/tickets";
  const { data: tickets, loading } = useApi<Ticket[]>(query);

  const statusColors: Record<string, string> = {
    open: "text-cyber-orange border-cyber-orange/30 bg-cyber-orange/10",
    in_progress: "text-electric-blue border-electric-blue/30 bg-electric-blue/10",
    resolved: "text-cyber-green border-cyber-green/30 bg-cyber-green/10",
    closed: "text-silver-dim border-dark-border bg-dark-border/30",
  };

  const priorityColors: Record<string, string> = {
    low: "text-silver-dim",
    medium: "text-cyber-orange",
    high: "text-cyber-red",
    urgent: "text-cyber-red",
  };

  const handleReply = async (ticketId: string, message: string) => {
    await api.post(`/tickets/${ticketId}/replies`, { message });
  };

  const handleStatusUpdate = async (ticketId: string, status: string) => {
    await api.patch(`/tickets/${ticketId}`, { status });
    // Refresh data
    window.location.reload();
  };

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Support Tickets</h1>
            <p className="text-silver text-sm mt-1">{tickets?.length || 0} total tickets</p>
          </div>
          <div className="flex gap-2">
            {["", "open", "in_progress", "resolved"].map((s) => (
              <button
                key={s || "all"}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  statusFilter === s ? "bg-electric-blue/20 text-electric-blue" : "text-silver-dim hover:text-white"
                }`}
              >
                {s ? s.replace("_", " ") : "All"}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-dark-border">
                    <th className="text-left text-silver-dim font-medium py-3 px-5">Ticket</th>
                    <th className="text-left text-silver-dim font-medium py-3 px-4">Status</th>
                    <th className="text-left text-silver-dim font-medium py-3 px-4">Priority</th>
                    <th className="text-left text-silver-dim font-medium py-3 px-4 hidden sm:table-cell">Created</th>
                    <th className="text-left text-silver-dim font-medium py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets?.map((ticket) => (
                    <tr key={ticket.id} className="border-b border-dark-border/50 hover:bg-dark-panel/50 transition-colors">
                      <td className="py-4 px-5">
                        <p className="text-white font-semibold text-sm">{ticket.subject}</p>
                        <p className="text-silver-dim text-xs mt-0.5 truncate max-w-md">{ticket.message}</p>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`text-xs px-2.5 py-1 rounded-full border ${statusColors[ticket.status]}`}>
                          {ticket.status.replace("_", " ")}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`text-xs font-medium ${priorityColors[ticket.priority]}`}>{ticket.priority}</span>
                      </td>
                      <td className="py-4 px-4 hidden sm:table-cell text-silver-dim text-xs">
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-4">
                        <button
                          onClick={() => {
                            setSelectedTicketId(ticket.id);
                            setViewModal(true);
                          }}
                          className="w-7 h-7 flex items-center justify-center rounded-lg border border-dark-border text-silver-dim hover:text-white hover:border-electric-blue/40 transition-all cursor-pointer"
                          title="View Ticket"
                        >
                          <i className="ri-eye-line text-xs" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <TicketModal
          open={viewModal}
          mode="view"
          ticketId={selectedTicketId || undefined}
          onClose={() => setViewModal(false)}
          onReply={handleReply}
          onUpdateStatus={handleStatusUpdate}
        />
      </div>
    </AdminLayout>
  );
}

